/**
 * Your Desk (the /desk page) wording and briefing rules — one place, unit-tested.
 * The page is "Desk Setup" until the company graduates, then "Your Next Move™".
 */
import { askAboutPrompt, deskMoveCopy, moveFacts, moveSide, NEXT_MOVE_LABEL, type DeskMove } from '@/lib/deskNextMove';

export const DESK_SETUP_TITLE = 'Desk Setup';

export function deskTitle(graduated: boolean): string {
  return graduated ? NEXT_MOVE_LABEL : DESK_SETUP_TITLE;
}

/** The user's own calendar day (local midnight), never UTC — UTC flips at 5pm in California. */
export function deskDay(d = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Who a card is about: its single named facts (a company), not counts or listing lists, so
 *  166 → 165 listings or an edited listing keeps the same brief, but Woodside and Headstone never share one. */
export function deskMoveEntity(move: DeskMove): string {
  if (move.kind !== 'move') return '';
  const vars = (move as { vars?: Record<string, unknown> }).vars ?? {};
  return Object.keys(vars).sort().flatMap((k) => {
    const v = vars[k];
    return typeof v === 'string' ? [`${k}=${v}`] : [];
  }).join(';');
}

/** One briefing per card (rule + who it is about) per local day; caught-up days get one too.
 *  Stored on the conversation in the cloud, so every device finds the same brief. */
export function deskBriefKey(userId: string, move: DeskMove | null, now = new Date()): string | null {
  if (!move) return null;
  const id = move.kind === 'caught_up' ? 'caught_up' : `${move.kind}:${move.key}:${deskMoveEntity(move)}`;
  return `your-desk-brief:${userId}:${id}:${deskDay(now)}`;
}

/** Where the user was on the desk. Only same-day positions resume; a new day opens on today's priority. */
export type DeskResume = { threadId: string; focus: 'priority' | 'other'; day: string };
export function resumeIfToday(v: unknown, now = new Date()): DeskResume | null {
  const r = v as Partial<DeskResume> | null;
  return r && typeof r.threadId === 'string' && r.day === deskDay(now) && (r.focus === 'priority' || r.focus === 'other')
    ? (r as DeskResume) : null;
}

/** Quiet this long and a page left open past midnight rolls to the new day by itself. */
export const DESK_IDLE_ROLLOVER_MS = 30 * 60_000;
/** Across midnight: roll over only when the user is not mid-work (idle, nothing typed, nothing writing). */
export function deskRollover(a: { startedDay: string; now: Date; lastActive: number; draft: string; streaming: boolean }): 'none' | 'roll' | 'offer' {
  if (a.startedDay === deskDay(a.now)) return 'none';
  const idle = a.now.getTime() - a.lastActive >= DESK_IDLE_ROLLOVER_MS;
  return idle && !a.draft.trim() && !a.streaming ? 'roll' : 'offer';
}

/** Pages that are Your Desk itself (old /my-day and /my-insights links land here). */
const DESK_PATHS = new Set(['/desk', '/my-day', '/my-insights']);
const pathOf = (to: string) => (to.startsWith('/') && !to.startsWith('//') ? new URL(to, 'https://cannafax.ai').pathname : null);

/** A card action that points at the page already open must answer in place — a link to "here" does nothing. */
export function actsInPlace(to: string | null, currentPath: string): boolean {
  const p = to ? pathOf(to) : null;
  if (!p) return false;
  return p === currentPath || (DESK_PATHS.has(p) && DESK_PATHS.has(currentPath));
}

const buyerCount = (move: DeskMove) => {
  const n = Number((move as { vars?: Record<string, unknown> }).vars?.n);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : 0;
};

/** What the card's button asks when it answers in place (e.g. "See who's looking" lists every buyer). */
export function deskInPlacePrompt(move: DeskMove | null): string | null {
  if (!move || move.kind !== 'move') return null;
  if (move.key === 'seller_buyers_looking') {
    const n = buyerCount(move);
    return `Show me every buyer company that looked at my menus in the last 24 hours${n ? ` — all ${n} of them` : ''}. For each: which menu, the listings they opened most, and when. Most engaged first, then who to contact first.`;
  }
  return askAboutPrompt(move);
}

/** The opening request: asks for the facts first and a concrete plan, never a blank greeting. */
export function deskBriefPrompt(move: DeskMove | null): string | null {
  if (!move) return null;
  if (move.kind === 'caught_up') return 'Nothing is flagged for me right now. What is one useful thing I could do today to sell more?';
  const copy = deskMoveCopy(move);
  const facts = moveFacts(move).filter((f) => !f.startsWith('Where to act'));
  const what = move.kind === 'setup' ? 'my next Desk Setup step' : 'my next move';
  const head = `Brief me on ${what}: "${copy.line}"${facts.length ? ` (${facts.join('; ')})` : ''}. I can already see that headline, so do not repeat it or list the facts back.`;
  // The card promises a count of buyers, so the brief names every one of them before picking who to call.
  if (move.kind === 'move' && move.key === 'seller_buyers_looking' && buyerCount(move) > 1) {
    return `${head} First list all ${buyerCount(move)} buyer companies that looked today, one line each with what they opened. Then in one or two sentences, say who to contact first and why.`;
  }
  return `${head} In two or three sentences, say who to contact first and why, then the one thing to do now.`;
}

/** Follow-up chips under the conversation. */
export function deskStarters(move: DeskMove | null): string[] {
  return [...baseStarters(move), OPEN_COMMITMENTS_STARTER, OVERNIGHT_STARTER];
}

/** At most this many chips sit above the Desk composer — a few good questions, never a wall. */
export const DESK_SUGGESTION_LIMIT = 4;

/**
 * The Desk tray, in order of relevance: questions about the card on screen first, then
 * questions about where the company stands, then admin-curated starters; capped and de-duplicated.
 * A fresh conversation (not about today's card) skips the card questions.
 */
export function deskSuggestions(
  move: DeskMove | null,
  admin: { text: string; id?: string }[],
  onPriority = true,
): { text: string; id?: string }[] {
  const card = onPriority && move && move.kind !== 'caught_up' ? baseStarters(move) : [];
  const state = card.length ? [] : [...baseStarters(null), OPEN_COMMITMENTS_STARTER, OVERNIGHT_STARTER];
  const seen = new Set<string>();
  const out: { text: string; id?: string }[] = [];
  for (const s of [...card.map((text) => ({ text })), ...state.map((text) => ({ text })), ...admin]) {
    const k = s.text.trim().toLowerCase();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(s);
    if (out.length === DESK_SUGGESTION_LIMIT) break;
  }
  return out;
}

function baseStarters(move: DeskMove | null): string[] {
  if (move?.kind === 'move' && moveSide(move) === 'seller') {
    const watch = move.key === 'seller_listing_watched' || move.key === 'seller_buyers_looking';
    return [watch ? 'Who should I call first?' : 'Why this one?', 'Draft a short message for them', 'What else is worth doing today?'];
  }
  if (move?.kind === 'move') return ['Why this one?', 'What should I ask the supplier?', 'What else is worth doing today?'];
  if (move?.kind === 'setup') return ['Why does this step matter?', 'Walk me through it', 'What comes after this?'];
  return ['Which buyers looked at my listings this week?', 'Which listings need a fresh price?'];
}

const BRIEF_PREFIX = 'Brief me on ';
const CAUGHT_UP_PROMPT = 'Nothing is flagged for me right now.';

/** The automatic briefing request is plumbing: the card above already says it, so the bubble is hidden. */
export function isDeskBriefPrompt(text: string): boolean {
  return text.startsWith(BRIEF_PREFIX) || text.startsWith(CAUGHT_UP_PROMPT);
}

/** Sidebar titles: name the conversation by its subject, not by the request that opened it. */
export function deskThreadTitle(title: string | null | undefined): string {
  const t = (title ?? '').trim();
  if (!t) return 'Untitled';
  if (t.startsWith(CAUGHT_UP_PROMPT)) return 'Ideas for today';
  if (!t.startsWith(BRIEF_PREFIX)) return t;
  const quoted = t.match(/"([^"]+)/)?.[1]?.replace(/\.\.\.$/, '').trim();
  if (!quoted) return 'Today’s priority';
  const s = quoted.replace(/\b([A-Z]{2,})\b/g, (w) => w.charAt(0) + w.slice(1).toLowerCase());
  return s.length > 48 ? `${s.slice(0, 47)}…` : s;
}

/** Sidebar history groups by the user's local day: Today, Yesterday, Previous 7 days, Older. */
export type ThreadGroup = 'Today' | 'Yesterday' | 'Previous 7 days' | 'Older';
export function threadGroup(iso: string, now = new Date()): ThreadGroup {
  const start = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((start(now) - start(new Date(iso))) / 86_400_000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days <= 7) return 'Previous 7 days';
  return 'Older';
}
export function groupThreads<T extends { updated_at: string }>(threads: T[], now = new Date()): { group: ThreadGroup; items: T[] }[] {
  const out: { group: ThreadGroup; items: T[] }[] = [];
  for (const t of threads) {
    const g = threadGroup(t.updated_at, now);
    if (out[out.length - 1]?.group === g) out[out.length - 1].items.push(t);
    else out.push({ group: g, items: [t] });
  }
  return out;
}


/** Today's deck holds at most this many moves. */
export const DECK_MAX = 5;

/** Skip goes to the next move and wraps back to the first, so a skipped move is never lost. */
export function nextDeckIndex(index: number, length: number): number {
  return length <= 0 ? 0 : (index + 1) % Math.min(length, DECK_MAX);
}

/** On-demand overnight summary, asked from a chip instead of a block of text. */
export const OVERNIGHT_STARTER = 'What happened overnight?';

/** Saved follow-ups are answered in chat, not opened as a separate list. */
export const OPEN_COMMITMENTS_STARTER = 'What promises do I have open?';
